import { Component, inject, OnInit } from "@angular/core";
import { FormBuilder, FormControl, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { DomainesService } from "src/app/shared/services/domaines.service";
import { ValeurDomaineDto } from "src/app/shared/models/domaines.dto";
import { MasquesService } from "src/app/shared/masques/masques.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SigtUtilisateurService, UtilisateurDetails } from "src/app/shared/services/sigt-utilisateur.service";
import { map } from "rxjs";
import { SigtChargementEnCoursService } from "src/app/integration/sigt-chargement-en-cours/sigt-chargement-en-cours.service";
import { Constantes } from "src/app/shared/constantes";
import { CommonModule } from "@angular/common";
import { NgxMaskDirective } from "ngx-mask";
import { PivPiedPageComponent } from "src/app/zzz/ui/piv-pied-page/piv-pied-page.component";

@Component({
    selector: "app-mes-informations",
    templateUrl: './mes-informations.component.html',
    styleUrls: ['./mes-informations.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        NgxMaskDirective,
        PivPiedPageComponent
    ]
})
export class MesInformationsComponent implements OnInit {
  #masqueService = inject(MasquesService);
  #formBuilder = inject(FormBuilder);
  #utilisateurService = inject(SigtUtilisateurService);
  #domainesService = inject(DomainesService);
  #chargementEnCoursService = inject(SigtChargementEnCoursService);
  #translateService = inject(TranslateService);

  estInscrit = this.#utilisateurService.current.estInscrit;
  utilisateurCourant = this.#utilisateurService.current;

  telephonePlaceholder = this.#masqueService.getPlaceholderTelephoneCanada();
  masqueNumeroTelephone = this.#masqueService.getMasqueTelephoneCanada();

  formulaire = this.#formBuilder.group({
    id: [null],
    type: [""],
    code: [""],
    numeroTelephone: ["", [Validators.required, Validators.maxLength(20), Validators.pattern(/^$|[0-9]{10}/)]],
    numeroPoste: ["", [Validators.maxLength(10)]],
    nomDirection: [""],
    nomMinistereOrganisme: ["", [Validators.required, Validators.maxLength(100)]],
    typeIntervenant: ["", [Validators.required]],
    nom: [""],
    prenom: [""],
    courriel: [""],
  }, {
    validators: this.oneValueHasToBeChangedValidator
  });

  listeTypeInter: ValeurDomaineDto[];
  listeTypeIntervenant$ = this.#domainesService.obtenirValeurs('TYPE_INTERVENANT').pipe(
    map(rep => rep.valeursDomaine)
  );

  messagesDeValidation: { [key: string]: string } = {};

  ngOnInit(): void {
    this.listeTypeIntervenant$.subscribe({
      next: valeurs => this.listeTypeInter = valeurs
    });

    this.formulaire.setValidators(this.oneValueHasToBeChangedValidator(this.utilisateurCourant));
    this.reinitialiserForm();

    this.messagesDeValidation.required = Constantes.MessageApplicatif.ValeurObligatoire_1E;
    this.messagesDeValidation.invalideTelephone = Constantes.MessageApplicatif.Invalide_2E;
    this.messagesDeValidation.maximumLengthNomEmployeur = Constantes.MessageApplicatif.MaximumCaracteres_28E;
  }

  get nomPrenom() { return this.utilisateurCourant?.nom + " " + this.utilisateurCourant?.prenom; }

  get typeEmployeur() {
    let resultToReturn = "";
    if (this.utilisateurCourant && this.utilisateurCourant.id !== -1 && this.listeTypeInter) {
      resultToReturn = this.listeTypeInter.find(f => f.valeur === this.utilisateurCourant.typeIntervenant).description;
    }
    return resultToReturn;
  }

  get erreurTelephoneRequired() {
    return this.messagesDeValidation.required?.replace('{0}',
      this.#translateService.instant('gestionUtilisateur.form.messagesPlaceholders.ph0InvalideTelephone'));
  }

  get erreurTelephoneInvalid() {
    return this.messagesDeValidation.invalideTelephone?.replace('{0}',
      this.#translateService.instant('gestionUtilisateur.form.messagesPlaceholders.ph0InvalideTelephone'));
  }

  get erreurTypeClienteleExterneRequired() {
    return this.messagesDeValidation.required?.replace('{0}',
      this.#translateService.instant('gestionUtilisateur.form.messagesPlaceholders.ph0RequiredTypeClienteleExterne'));
  }

  get erreurNomEmployeurRequired() {
    return this.messagesDeValidation.required?.replace('{0}',
      this.#translateService.instant('gestionUtilisateur.form.messagesPlaceholders.ph0RequiredNomEmployeur'));
  }

  get erreurNomEmployeurMaxLength() {
    return this.messagesDeValidation.maximumLengthNomEmployeur?.replace('{0}',
      this.#translateService.instant('gestionUtilisateur.form.messagesPlaceholders.ph0MaxLengthNomEmployeur'));
  }

  reinitialiserForm(): void {
    this.formulaire.patchValue({
      id: this.utilisateurCourant.id,
      type: this.utilisateurCourant.type,
      code: this.utilisateurCourant.code,
      numeroTelephone: this.utilisateurCourant.numeroTelephone,
      numeroPoste: this.utilisateurCourant.numeroPoste,
      nomDirection: this.utilisateurCourant.nomDirection,
      nomMinistereOrganisme: this.utilisateurCourant.nomMinistereOrganisme,
      typeIntervenant: this.utilisateurCourant.typeIntervenant,
      nom: this.utilisateurCourant.nom,
      prenom: this.utilisateurCourant.prenom,
      courriel: this.utilisateurCourant.courriel,
    });
  }

  enregistrerForm() {
    if (this.formulaire.valid) {
      this.#chargementEnCoursService.afficher(true);

      this.#utilisateurService.updateUtilisateur(this.formulaire.value).subscribe({
        next: _ => window.location.href = window.location.href
      });
    }
  }

  oneValueHasToBeChangedValidator(initialValue: UtilisateurDetails): ValidatorFn {
    return (form: FormControl): { [key: string]: any } => {
      let sameValues = true;
      if (initialValue) {
        Object.keys(form.value).forEach(k => {
          if (form.value[k] !== initialValue[k]) {
            sameValues = false;
            return;
          }
        });
      }

      return sameValues ? { 'sameValues': true } : null;
    };
  }

  chiffreUniquement(evenement): boolean {
    const monCaractere = (evenement.which) ? evenement.which : evenement.code;
    if (monCaractere > 31 && (monCaractere < 47 || monCaractere > 57)) {
      return false;
    }
    return true;
  }
}
